import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  TextField,
} from '@mui/material'
import VerifiedIcon from '@mui/icons-material/Verified'
import { SelectChangeEvent } from '@mui/material/Select'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateValidationError } from '@mui/x-date-pickers/models'
import { DateField } from '@mui/x-date-pickers/DateField'
import { getCustomerInfo } from 'src/helpers/lmnHelpers'
import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import LmnBackButton from 'src/components/Dialogs/Lmn/common/LmnBackButton'
import {
  useContext,
  useState,
  useEffect,
  useMemo,
  ChangeEvent,
} from 'react'
import { useSelector } from '@xstate/react'
import styles from 'src/components/Lmn/Lmn.scss'
import stateData from 'src/assets/data/states.json'
import mixpanel from 'mixpanel-browser'

// Date of birth must be 18+
const eighteenYearsAgo = dayjs().subtract(18, 'year')
const setDashInPhoneNumber = (phoneNumber: string): string => {
  const phoneWithoutPrefix = phoneNumber.replace('+1', '')
  const newValueArr = [
    phoneWithoutPrefix.slice(0, 3),
    phoneWithoutPrefix.slice(3, 6),
    phoneWithoutPrefix.slice(6, 10),
  ].filter((x) => x)
  return newValueArr.join('-')
}

const LmnIntakeForm = (): JSX.Element => {
  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService
  const {
    firstName,
    lastName,
    email,
    confirmEmail,
    sex,
    postalCode,
    code,
    city,
    addressLine1,
    addressLine2,
    state,
    dateOfBirth,
    phone: phoneNumber,
  } = useSelector(lmnContextService, getCustomerInfo)

  const [phone, setPhone] = useState('')
  const [dateOfBirthDayjs, setDateOfBirthDayjs] = useState<Dayjs | null>(null)
  const [error, setError] = useState(false)
  const [dateOfBirthError, setDateOfBirthError] = useState<DateValidationError | null>(null)

  const handleDateOfBirthChanged = (
    newValue: Dayjs,
    context: { validationError: DateValidationError },
  ): void => {
    setDateOfBirthDayjs(newValue)
    setDateOfBirthError(context.validationError)

    const dob = newValue.format('MM/DD/YYYY')

    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        dateOfBirth: dob,
      },
    })
  }

  const handleSelectChanged = (event: SelectChangeEvent, field: string): void => {
    const fieldValue = event.target.value
    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        [field]: fieldValue,
      },
    })
  }

  const handleTextValueChanged = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ): void => {
    const fieldValue = event.target.value
    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        [field]: (field === 'email' || field === 'confirmEmail') ? fieldValue.trim() : fieldValue,
      },
    })
  }

  const handlePhoneChanged = ({
    target,
  }: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    const newInput = target.value.replace(/\D/g, '')
    const fieldValue = setDashInPhoneNumber(newInput)
    setPhone(fieldValue)

    const phoneWithoutHyphens = `+1${fieldValue.replace(/-/g, '')}`
    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        phone: phoneWithoutHyphens,
      },
    })
  }

  const handlePostalCodeChanged = ({
    target,
  }: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    // replace anything not a number
    let newValue = target.value.replace(/\D/g, '')
    // remove anything after 5th digit
    newValue = newValue.replace(/(?<=^.{5}).*/g, '')
    send({
      type: 'SET_CUSTOMER_INFO',
      data: {
        postalCode: newValue,
      },
    })
  }

  type StateAbbr = keyof typeof stateData
  const stateDataValues: StateAbbr[] = Object.keys(stateData) as StateAbbr[]

  const firstNameInvalid = firstName.trim() === ''
  const lastNameInvalid = lastName.trim() === ''
  // NOTE: pattern taken from here https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
  // eslint-disable-next-line no-control-regex
  const regexEmailPattern = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
  const emailInvalid = !regexEmailPattern.test(email)
  const confirmEmailInvalid: boolean = email !== confirmEmail
  const phoneInvalid = phone.length < 12 || ['0', '1'].includes(phone[0])
  const sexInvalid = sex === ''
  // NOTE: pattern taken from here https://stackoverflow.com/questions/160550/zip-code-us-postal-code-validation
  const postalCodeInvalid = !(/(^\d{5}$)/.test(postalCode))
  const addressLine1Invalid = addressLine1.trim() === ''
  const cityInvalid = city.trim() === ''
  const stateInvalid = state === ''
  const dateOfBirthInvalid = !!dateOfBirthError || !dateOfBirthDayjs?.isValid()

  const dateOfBirthErrorMessage = useMemo(() => (
    (error && dateOfBirthInvalid) ? 'Please enter a valid DOB.' : ''
  ), [error, dateOfBirthInvalid])

  const handleNextStepClicked = (): void => {
    if (firstNameInvalid || lastNameInvalid || emailInvalid || confirmEmailInvalid || phoneInvalid
      || sexInvalid || postalCodeInvalid || addressLine1Invalid || cityInvalid
      || stateInvalid || dateOfBirthInvalid) {
      setError(true)
      return
    }

    setError(false)

    send({
      type: 'SEND_OTP',
      data: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        city: city.trim(),
      },
    })
    mixpanel.track('completed_lmn_patient_profile')
  }

  useEffect(() => {
    const phoneWithoutPrefix = setDashInPhoneNumber(phoneNumber)
    // Repopulating info if they go back to this screen
    setPhone(phoneWithoutPrefix)
    setDateOfBirthDayjs(dayjs(dateOfBirth))
    mixpanel.track('view_lmn_patient_profile')
    // only running this once on component load w/ empty array
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  return (
    <>
      <Box display="flex" flexDirection="column" paddingBottom="24px">
        <Typography variant="h4Bold">Tell us about yourself</Typography>
        <Typography variant="body3" color="gray1.main">
          Please ensure that all fields are complete and match the information
          on your HSA/FSA account.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Typography variant="body3Bold" marginBottom="8px">Full Name</Typography>
        <Typography variant="body3" color="gray1.main" marginBottom="8px">
          Must match patient&apos;s photo ID and the name on HSA/FSA account.
        </Typography>
        <Box display="flex" gap="10px">
          <TextField
            placeholder="First Name"
            error={error && firstNameInvalid}
            helperText={(error && firstNameInvalid) && 'Please enter a valid first name.'}
            size="small"
            variant="outlined"
            value={firstName}
            onChange={(event) => handleTextValueChanged(event, 'firstName')}
            className={styles.lmnTextFieldHelper}
          />
          <TextField
            placeholder="Last Name"
            error={error && lastNameInvalid}
            helperText={(error && lastNameInvalid) && 'Please enter a valid last name.'}
            size="small"
            variant="outlined"
            value={lastName}
            onChange={(event) => handleTextValueChanged(event, 'lastName')}
            className={styles.lmnTextFieldHelper}
          />
        </Box>
        <Typography variant="body3Bold" className={styles.lmnFieldLabel}>E-mail</Typography>
        <Box display="flex" flexDirection="column" gap="10px">
          <TextField
            error={error && (emailInvalid || confirmEmailInvalid)}
            helperText={error && emailInvalid && 'Please enter a valid email address.'}
            type="email"
            size="small"
            placeholder="E-mail"
            variant="outlined"
            value={email}
            onChange={(event) => handleTextValueChanged(event, 'email')}
            className={styles.lmnTextFieldHelper}
          />
          <TextField
            error={error && confirmEmailInvalid}
            helperText={error && confirmEmailInvalid && 'Please confirm that both emails match.'}
            type="email"
            size="small"
            placeholder="Confirm E-mail"
            variant="outlined"
            value={confirmEmail}
            onChange={(event) => handleTextValueChanged(event, 'confirmEmail')}
            className={styles.lmnTextFieldHelper}
          />
        </Box>
        <Typography variant="body3Bold" className={styles.lmnFieldLabel}>Phone</Typography>
        {code !== '' ? (
          <Typography variant="body2" style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '4px' }}>+1</span>
            {phone}
            <VerifiedIcon fontSize="small" color="success" style={{ marginLeft: '12px', marginRight: '4px' }} />
            Verified
          </Typography>
        ) : (
          <TextField
            error={error && phoneInvalid}
            helperText={(error && phoneInvalid) && 'Please enter a valid phone US phone number.'}
            type="tel"
            size="small"
            placeholder="000-000-0000"
            InputProps={{
              startAdornment: <InputAdornment position="start">+1</InputAdornment>,
            }}
            variant="outlined"
            value={phone}
            onChange={handlePhoneChanged}
            className={styles.lmnTextFieldHelper}
          />
        )}
        <Box display="flex" flexDirection="row" gap="10px">
          <Box display="flex" flexDirection="column" width="50%">
            <Typography variant="body3Bold" className={styles.lmnFieldLabel}>Date of Birth</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateField
                disableFuture
                size="small"
                value={dateOfBirthDayjs}
                maxDate={eighteenYearsAgo}
                slotProps={{
                  textField: {
                    helperText: dateOfBirthErrorMessage,
                    error: error && dateOfBirthInvalid,
                  },
                }}
                className={styles.lmnTextFieldHelper}
                onChange={handleDateOfBirthChanged}
              />
            </LocalizationProvider>
          </Box>
          <Box display="flex" flexDirection="column" width="50%">
            <Typography variant="body3Bold" className={styles.lmnFieldLabel}>Sex</Typography>
            <FormControl fullWidth size="small" error={error && sexInvalid}>
              <Select
                error={error && sexInvalid}
                id="sex-select"
                value={sex}
                variant="outlined"
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ opacity: 0.42 }}>Select</span>
                  }
                  return selected
                }}
                onChange={(event) => handleSelectChanged(event, 'sex')}
              >
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
              {error && sexInvalid ? (
                <FormHelperText
                  sx={{
                    fontSize: '14px',
                    marginLeft: 0,
                    marginRight: 0,
                  }}
                >
                  Please select a sex.
                </FormHelperText>
              ) : null}
            </FormControl>
          </Box>
        </Box>
        <Typography variant="body3Bold" className={styles.lmnFieldLabel}>Address</Typography>
        <Box display="flex" flexDirection="column" gap="10px">
          <TextField
            error={error && addressLine1Invalid}
            helperText={(error && addressLine1Invalid) && 'Please enter an address.'}
            placeholder="Address"
            size="small"
            variant="outlined"
            value={addressLine1}
            onChange={(event) => handleTextValueChanged(event, 'addressLine1')}
            className={styles.lmnTextFieldHelper}
          />
          <TextField
            placeholder="Apartment, suite, etc. (opt)"
            size="small"
            variant="outlined"
            value={addressLine2}
            onChange={(event) => handleTextValueChanged(event, 'addressLine2')}
          />
          <TextField
            error={error && cityInvalid}
            helperText={(error && cityInvalid) && 'Please enter a city.'}
            placeholder="City"
            size="small"
            variant="outlined"
            value={city}
            onChange={(event) => handleTextValueChanged(event, 'city')}
            className={styles.lmnTextFieldHelper}
          />
          <Box display="flex" flexDirection="row" gap="10px">
            <FormControl fullWidth size="small" error={error && stateInvalid}>
              <Select
                error={error && stateInvalid}
                id="state-select"
                value={state}
                variant="outlined"
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ opacity: 0.42 }}>State</span>
                  }
                  return selected
                }}
                onChange={(event) => handleSelectChanged(event, 'state')}
              >
                {stateDataValues.map((item) => (
                  <MenuItem value={item} key={item}>{stateData[item]}</MenuItem>
                ))}
              </Select>
              {(error && stateInvalid) ? (
                <FormHelperText
                  sx={{
                    fontSize: '14px',
                    marginLeft: 0,
                    marginRight: 0,
                  }}
                >
                  Please select a state.
                </FormHelperText>
              ) : null}
            </FormControl>
            <TextField
              error={error && postalCodeInvalid}
              helperText={(error && postalCodeInvalid) && 'Please enter a valid zip code.'}
              placeholder="Zip Code"
              size="small"
              variant="outlined"
              value={postalCode}
              onChange={handlePostalCodeChanged}
              className={styles.lmnTextFieldHelper}
            />
          </Box>
        </Box>
        <Typography variant="body3" color="gray1.main" marginTop="24px">
          After you press continue, we will send a text message to verify your phone number.
        </Typography>
        <Button
          className={styles.lmnButton}
          color="primary"
          fullWidth
          size="large"
          variant="contained"
          onClick={handleNextStepClicked}
        >
          <Typography variant="body3Bold">Continue</Typography>
        </Button>
        <LmnBackButton />
      </Box>
    </>
  )
}

export default LmnIntakeForm
